export function mapPermissions(layout, canEdit, lockedFieldNames = []) {
  const lockedFields = new Set(lockedFieldNames);
  return layout.map((rowGroup) => ({
    row: rowGroup.row.map((field) => ({
      ...field,
      disabled: field.disabled || lockedFields.has(field.name) ? true : !canEdit,
    })),
  }));
}

export function validateForm({ dynamicLayout, formData }) {
  const errors = {};

  dynamicLayout.forEach((row) => {
    row.row.forEach((field) => {
      if (field.required) {
        const value = formData[field.name];
        const isEmpty = value === undefined || value === null || value === "";

        if (isEmpty) {
          errors[field.name] = "This field is required.";
        }
      }
    });
  });

  return errors;
}

export function applyConditionalRequiredRules(fieldName, layout, formData, validationRules) {
  const dynamicallyRequiredFields = new Set();

  for (const [triggerField, rules] of Object.entries(validationRules)) {
    // Support for both single rule and array of rules
    const normalizedRules = Array.isArray(rules) ? rules : [rules];

    // Validate rules
    if (fieldName === null || fieldName === triggerField) {
      const triggerValue = formData[triggerField];

      normalizedRules.forEach((rule) => {
        if (rule.oneOf.includes(triggerValue)) {
          rule.required.forEach((requiredField) => dynamicallyRequiredFields.add(requiredField));
        }
      });
    }
  }

  // Update layout with required fields
  return layout.map((rowLayout) => ({
    ...rowLayout,
    row: rowLayout.row.map((field) => {
      const wasInitiallyRequired =
        field.initialRequired !== undefined ? field.initialRequired : !!field.required;

      const isNowRequired = dynamicallyRequiredFields.has(field.name) || wasInitiallyRequired;

      return {
        ...field,
        required: isNowRequired,
        initialRequired: wasInitiallyRequired,
      };
    }),
  }));
}

export function applyFieldTransformations(triggerField, layout, formData, transformRules) {
  const applyTransforms = (currentLayout, fieldName) => {
    const triggerValue = formData[fieldName];
    const transforms = transformRules?.[fieldName]?.[triggerValue];
    if (!transforms) {
      return currentLayout;
    }

    return currentLayout.map((row) => ({
      ...row,
      row: row.row.map((field) => {
        const transformation = transforms.find((t) => t?.target === field?.referenceName);
        if (!transformation) {
          return field;
        }

        const changes = transformation?.changes || {};

        return {
          ...field,
          ...changes,
          originalName: field.name,
        };
      }),
    }));
  };

  // Basic execution logic
  if (triggerField) {
    return applyTransforms(layout, triggerField);
  }

  // First execution logic
  return Object.keys(transformRules).reduce((accLayout, ruleField) => {
    return applyTransforms(accLayout, ruleField);
  }, layout);
}
